import React, { useState, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  InvoiceRecord,
  getBilledYearMonth,
  invoiceTrendLineAmount,
  defaultClientInvoiceCurrencyView,
  invoiceInAnyCalendarSelection,
  invoiceInFYSelection,
} from '../../../../utils/invoiceUtils';
import { ClientDetailData } from '../../../../hooks/useClientDetail';
import type { ClientDetailSharedPeriod } from './client-detail-period-types';

/* ─── Props ──────────────────────────────────────────────────────────────── */

interface ChartsAndTrendsTabProps {
  data: ClientDetailData;
  sharedPeriod: ClientDetailSharedPeriod;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const fmtAmount = (n: number, ccy: string): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const sym = ccy === 'USD' ? '$' : `${ccy} `;
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${sym}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtAxisTick = (value: number, ccy: string): string => {
  const sym = ccy === 'USD' ? '$' : `${ccy} `;
  const abs = Math.abs(value);
  if (abs >= 1_000) return `${sym}${(value / 1_000).toFixed(0)}K`;
  return `${sym}${value.toFixed(0)}`;
};

/* ─── Component ──────────────────────────────────────────────────────────── */

export const ChartsAndTrendsTab: React.FC<ChartsAndTrendsTabProps> = ({
  data,
  sharedPeriod,
}) => {
  const invoiceRecords: InvoiceRecord[] = data?.detailed_invoices?.invoice_records || [];
  const baseCurrency =
    data?.metadata?.original_currency ||
    data?.currency_analysis?.primary_currency ||
    'USD';

  const currencyView = useMemo(
    () => defaultClientInvoiceCurrencyView(data),
    [data],
  );
  const [showTable, setShowTable] = useState(false);

  const ccy = currencyView === 'USD' ? 'USD' : baseCurrency;

  /* ── Build trend data: group by billed month ─────────────────── */
  const trendData = useMemo(() => {
    const mode = currencyView;
    const amountByYm: Record<string, number> = {};

    for (const inv of invoiceRecords) {
      if (!inv.invoice_date) continue;
      const useCal =
        sharedPeriod.calendarYears.length > 0 || sharedPeriod.calendarMonths.length > 0;
      const useFy = sharedPeriod.fyYears.length > 0;
      if (useCal && !invoiceInAnyCalendarSelection(
        inv,
        sharedPeriod.calendarYears,
        sharedPeriod.calendarMonths,
      )) {
        continue;
      }
      if (useFy && !invoiceInFYSelection(inv, sharedPeriod.fyYears, sharedPeriod.fyQuarters)) {
        continue;
      }
      const ym = getBilledYearMonth(inv.invoice_date);
      if (!ym) continue;

      amountByYm[ym] = (amountByYm[ym] || 0) + invoiceTrendLineAmount(inv, mode);
    }

    const sortedKeys = Object.keys(amountByYm).sort();
    let cumulative = 0;
    return sortedKeys.map((ym, idx) => {
      const amount = Math.round(amountByYm[ym] * 100) / 100;
      cumulative += amount;
      const prevAmount = idx > 0 ? amountByYm[sortedKeys[idx - 1]] : 0;
      const mom = prevAmount > 0 ? ((amount - prevAmount) / prevAmount) * 100 : null;
      return {
        month: ym,
        amount,
        cumulative: Math.round(cumulative * 100) / 100,
        mom: mom != null ? Math.round(mom * 10) / 10 : null,
      };
    });
  }, [invoiceRecords, currencyView, sharedPeriod]);

  /* ── Custom tooltip ──────────────────────────────────────────── */
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ value: number; name: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
        <p className="font-semibold text-slate-800 mb-1">{label}</p>
        <p className="text-slate-600">
          Invoiced: <span className="font-bold text-slate-800">{fmtAmount(p?.value || 0, ccy)}</span>
        </p>
        {payload[1] && (
          <p className="text-slate-500">
            Cumulative: <span className="font-medium">{fmtAmount(payload[1].value, ccy)}</span>
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Monthly Invoice Trend ({ccy}) · Billed Month
          </span>
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg border transition-colors ${
              showTable
                ? 'bg-slate-800 text-white border-slate-800'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            {showTable ? '↗ Chart' : '⊞ Table'}
          </button>
        </div>
      </div>

      {trendData.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md p-12 text-center">
          <p className="text-slate-600 text-sm">No invoice data for the selected period.</p>
        </div>
      ) : showTable ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Month (Billed)</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoiced</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cumulative</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">MoM %</th>
                </tr>
              </thead>
              <tbody>
                {trendData.map((row, idx) => (
                  <tr
                    key={row.month}
                    className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'} hover:bg-blue-50/60`}
                  >
                    <td className="px-3 py-2.5 text-slate-700 font-medium tabular-nums">{row.month}</td>
                    <td className="px-3 py-2.5 text-right text-slate-800 tabular-nums font-semibold">{fmtAmount(row.amount, ccy)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums">{fmtAmount(row.cumulative, ccy)}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${
                      row.mom == null ? 'text-slate-300' :
                      row.mom > 0 ? 'text-emerald-600' :
                      row.mom < 0 ? 'text-red-500' : 'text-slate-600'
                    }`}>
                      {row.mom == null ? '—' : `${row.mom > 0 ? '+' : ''}${row.mom.toFixed(1)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(v) => fmtAxisTick(v, ccy)}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => fmtAxisTick(v, ccy)}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="amount"
                  name="Invoiced"
                  fill="#3b82f6"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative"
                  name="Cumulative"
                  stroke="#a855f7"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartsAndTrendsTab;
